import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import supabase from '../utils/supabaseClient';
import './AdminDashboard.css';

const AdminDashboard = () => {
    const { isDarkTheme } = useTheme();
    const navigate = useNavigate();

    const [stats, setStats] = useState({
        totalLessons: 0,
        approvedLessons: 0,
        unreviewedLessons: 0,
        totalExercises: 0,
        approvedExercises: 0,
        unreviewedExercises: 0,
        totalProblems: 0,
        approvedProblems: 0,
        unreviewedProblems: 0,
        completedUnits: 0,
    });
    const [latestChanges, setLatestChanges] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchStats = useCallback(async () => {
        setLoading(true);
        try {
            const [
                { count: totalLessons },
                { count: approvedLessons },
                { count: unreviewedLessons },
                { count: totalExercises },
                { count: approvedExercises },
                { count: unreviewedExercises },
                { count: totalProblems },
                { count: approvedProblems },
                { count: unreviewedProblems },
                { count: completedUnits },
            ] = await Promise.all([
                supabase.from('lessons').select('*', { count: 'exact', head: true }),
                supabase.from('lessons').select('*', { count: 'exact', head: true }).eq('status', 'approved'),
                supabase.from('lessons').select('*', { count: 'exact', head: true }).neq('status', 'approved'),
                supabase.from('exercises').select('*', { count: 'exact', head: true }),
                supabase.from('exercises').select('*', { count: 'exact', head: true }).eq('status', 'approved'),
                supabase.from('exercises').select('*', { count: 'exact', head: true }).neq('status', 'approved'),
                supabase.from('problems').select('*', { count: 'exact', head: true }),
                supabase.from('problems').select('*', { count: 'exact', head: true }).eq('status', 'approved'),
                supabase.from('problems').select('*', { count: 'exact', head: true }).neq('status', 'approved'),
                supabase.from('units').select('*', { count: 'exact', head: true }).eq('completed', true),
            ]);

            setStats({
                totalLessons: totalLessons || 0,
                approvedLessons: approvedLessons || 0,
                unreviewedLessons: unreviewedLessons || 0,
                totalExercises: totalExercises || 0,
                approvedExercises: approvedExercises || 0,
                unreviewedExercises: unreviewedExercises || 0,
                totalProblems: totalProblems || 0,
                approvedProblems: approvedProblems || 0,
                unreviewedProblems: unreviewedProblems || 0,
                completedUnits: completedUnits || 0,
            });

            const { data: recentExercises } = await supabase
                .from('exercises')
                .select('id, title, created_at, exercise_type')
                .order('created_at', { ascending: false })
                .limit(3);

            const { data: recentLessons } = await supabase
                .from('lessons')
                .select('id, title, created_at')
                .order('created_at', { ascending: false })
                .limit(3);

            const combined = [
                ...(recentExercises || []).map(e => ({ ...e, kind: 'Exercise' })),
                ...(recentLessons || []).map(l => ({ ...l, kind: 'Lesson' })),
            ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 5);

            setLatestChanges(combined);
        } catch (err) {
            console.error('[AdminDashboard] Error fetching stats:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    const statCards = [
        {
            label: 'Lessons',
            total: stats.totalLessons,
            approved: stats.approvedLessons,
            unreviewed: stats.unreviewedLessons,
            color: '#a78bfa',
        },
        {
            label: 'Exercises',
            total: stats.totalExercises,
            approved: stats.approvedExercises,
            unreviewed: stats.unreviewedExercises,
            color: '#4ade80',
        },
        {
            label: 'Problems',
            total: stats.totalProblems,
            approved: stats.approvedProblems,
            unreviewed: stats.unreviewedProblems,
            color: '#facc15',
        },
    ];

    return (
        <div className={`admin-dashboard ${isDarkTheme ? 'dark' : 'light'}`}>
            {loading ? (
                <div className="admin-loading">Loading dashboard...</div>
            ) : (
                <>
                    <section className="admin-stats-grid">
                        {statCards.map((card) => (
                            <div key={card.label} className="admin-stat-card card">
                                <div className="admin-stat-header">
                                    <h3>{card.label}</h3>
                                    <span className="admin-stat-total" style={{ color: card.color }}>
                                        {card.total}
                                    </span>
                                </div>
                                <div className="admin-stat-breakdown">
                                    <div className="admin-stat-row">
                                        <span className="admin-stat-dot approved" />
                                        <span className="admin-stat-label">Approved</span>
                                        <span className="admin-stat-value">{card.approved}</span>
                                    </div>
                                    <div className="admin-stat-row">
                                        <span className="admin-stat-dot unreviewed" />
                                        <span className="admin-stat-label">Not reviewed</span>
                                        <span className="admin-stat-value">{card.unreviewed}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </section>

                    <section className="admin-bottom-grid">
                        <div className="admin-latest-card card">
                            <h3>Latest Changes</h3>
                            {latestChanges.length > 0 ? (
                                <div className="admin-latest-list">
                                    {latestChanges.map((item) => (
                                        <button
                                            key={`${item.kind}-${item.id}`}
                                            className="admin-latest-item"
                                            type="button"
                                            onClick={() => navigate(item.kind === 'Lesson' ? `/admin/lessons/${item.id}/edit` : `/admin/exercises/${item.id}/edit`)}
                                        >
                                            <span className={`admin-latest-badge ${item.kind.toLowerCase()}`}>
                                                {item.kind}
                                            </span>
                                            <span className="admin-latest-title">{item.title}</span>
                                            <span className="admin-latest-date">
                                                {new Date(item.created_at).toLocaleDateString()}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <p className="admin-empty-text">No recent changes yet.</p>
                            )}
                        </div>

                        <div className="admin-units-card card">
                            <h3>Completed Units</h3>
                            <div className="admin-units-count">{stats.completedUnits}</div>
                            <p className="admin-units-caption">units fully completed</p>
                        </div>
                    </section>

                    <section className="admin-actions-row">
                        <button
                            className="admin-action-btn primary"
                            onClick={() => navigate('/admin/exercises/new')}
                        >
                            + Create Exercise
                        </button>
                        <button
                            className="admin-action-btn secondary"
                            onClick={() => navigate('/admin/lessons/new')}
                        >
                            + Create Lesson
                        </button>
                        <button
                            className="admin-action-btn secondary"
                            onClick={() => navigate('/admin/exercises?view=lessons')}
                        >
                            View Lessons
                        </button>
                    </section>
                </>
            )}
        </div>
    );
};

export default AdminDashboard;
